import { useEffect, useState, type FC } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { IconMenu2, IconSettings, IconStopwatch } from '@tabler/icons-react';

import {
  Banner,
  BannerBrandSuite,
  BannerBrandLogo,
  BannerBrandInline,
  BannerSearch,
  BannerRight,
  Fishbowl,
  ButtonFieldIcon,
  Iconsvg,
  SrOnly,
  BannerStatus,
} from '@wk/components-react16';
import { useMsal } from '@azure/msal-react';

import { InteractionStatus } from '@azure/msal-browser';

import { useShallow } from 'zustand/react/shallow';

import { callMsGraph } from '@/lib/MsGraphApi';
import useOnGoingEvaluationsStore from '@/features/ongoingEvaluations/stores/ongoingEvaluationsStore';

import type { GraphData } from '@/types';

import './Header.css';

interface HeaderState {
  graphData: GraphData | null;
  userPhoto: string | null;
  isFetchingGraphData: boolean;
  hasUserPhoto: boolean;
}

interface HeaderProps {
  showMobileMenuButton?: boolean;
  onMobileMenuClick?: () => void;
  settingsPath?: string | ((pathname: string) => string);
  productName?: string;
  childProductName?: string;
  showSettingsButton?: boolean;
}

const Header: FC<HeaderProps> = ({
  showMobileMenuButton = false,
  onMobileMenuClick,
  settingsPath = '/settings',
  productName = 'Pegasus',
  childProductName = 'Operational Readiness Review',
  showSettingsButton = true,
}) => {
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState<HeaderState>({
    graphData: null,
    userPhoto: null,
    isFetchingGraphData: false,
    hasUserPhoto: false,
  });

  const handleStateUpdate = (newState: Partial<HeaderState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const { ongoingEvaluations, setIsOngoingEvaluationsOpen } =
    useOnGoingEvaluationsStore(
      useShallow((state) => ({
        ongoingEvaluations: state.ongoingEvaluations,
        setIsOngoingEvaluationsOpen: state.setIsOngoingEvaluationsOpen,
      })),
    );

  const mobileMenuLabel = 'Open filters';

  useEffect(() => {
    let objectUrl: string | undefined;
    if (!state.graphData && inProgress === InteractionStatus.None) {
      handleStateUpdate({ isFetchingGraphData: true });
      callMsGraph()
        .then(([graphData, userPhotoBlob, hasPhoto]) => {
          if (hasPhoto && userPhotoBlob) {
            objectUrl = (window.URL || window.webkitURL).createObjectURL(
              userPhotoBlob,
            );
          }
          handleStateUpdate({
            graphData,
            userPhoto: objectUrl ?? null,
            hasUserPhoto: !!objectUrl,
            isFetchingGraphData: false,
          });
        })
        .catch(() => {
          handleStateUpdate({
            isFetchingGraphData: false,
            userPhoto: null,
            hasUserPhoto: false,
          });
        });
    }
  }, [inProgress, instance, state.graphData]);

  return (
    <Banner mode={'workflow'}>
      <BannerBrandSuite
        srLabel={`${productName} ${childProductName}`}
        isPreventDefault
        onBrandClick={() => {
          navigate('/');
        }}
        slot="bannerBrandSuite"
      >
        <BannerBrandLogo
          xsLogoSrc={
            'https://cdn.wolterskluwer.io/wk/jumpstart-v3-assets/0.x.x/logo/wheel-small.svg'
          }
          logoSrc={
            'https://cdn.wolterskluwer.io/wk/jumpstart-v3-assets/0.x.x/logo/wheel-medium.svg'
          }
          slot="bannerBrandLogo"
        ></BannerBrandLogo>
        <BannerBrandInline
          productName={productName}
          childProductName={childProductName}
          slot="bannerBrand"
        ></BannerBrandInline>
      </BannerBrandSuite>
      <BannerSearch slot="bannerSearch">
        <div slot="bannerSearchMobile" className="cg-header-mobile-search-slot">
          {showMobileMenuButton ? (
            <ButtonFieldIcon size="large24px">
              <button
                type="button"
                id="menuMobile"
                aria-label={mobileMenuLabel}
                onClick={onMobileMenuClick}
              >
                <IconMenu2 size={24} />
                <SrOnly>{mobileMenuLabel}</SrOnly>
              </button>
            </ButtonFieldIcon>
          ) : null}
          <button
            type="button"
            className="cg-header-mobile-home-logo"
            aria-label="Go to home"
            onClick={() => {
              navigate('/');
            }}
          >
            <img
              src="https://cdn.wolterskluwer.io/wk/jumpstart-v3-assets/0.x.x/logo/wheel-small.svg"
              alt="Wolters Kluwer"
            />
          </button>
        </div>
      </BannerSearch>
      <BannerRight slot="bannerRight">
        {showSettingsButton ? (
          <ButtonFieldIcon>
            <button
              type="button"
              onClick={() => {
                navigate(
                  typeof settingsPath === 'function'
                    ? settingsPath(location.pathname)
                    : settingsPath,
                );
              }}
            >
              <IconSettings size={24} />
            </button>
          </ButtonFieldIcon>
        ) : null}
        <ButtonFieldIcon>
          <button
            type="button"
            className="relative"
            onClick={() => {
              setIsOngoingEvaluationsOpen(true);
            }}
          >
            <IconStopwatch size={24} />
            {ongoingEvaluations.length > 0 && (
              <span className="absolute top-3 right-3 animate-pulse bg-red-500 text-white text-xs rounded-full w-2 h-2 flex items-center justify-center"></span>
            )}
          </button>
        </ButtonFieldIcon>
        {state.isFetchingGraphData ? (
          <>
            <BannerStatus
              status={'pending'}
              srLabel={'Fetching user details'}
            ></BannerStatus>
          </>
        ) : (
          <>
            <Fishbowl
              userName={state.graphData?.displayName ?? ''}
              onFishbowlClick={() => { }}
              hasIcon={!state.hasUserPhoto}
            >
              {state.hasUserPhoto ? (
                <img
                  alt={state.graphData?.displayName ?? ''}
                  src={state.userPhoto ?? ''}
                />
              ) : (
                <Iconsvg name="user" isFill />
              )}
            </Fishbowl>
          </>
        )}
      </BannerRight>
    </Banner>
  );
};

export default Header;
